import React, { useCallback, useEffect } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { useState } from 'react';
import MyDataTable from '../../../components/MyDataTable';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import { ClonedPlateParamList } from '../../../typings/Params';
import { GenericPaginatedParameters } from '../../../services/central-api/base-service';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { clonedPlateService } from '../../../services/central-api/cloned-plate';
import moment from 'moment';
import CloneSuspicionModal from './CloneSuspicionModal';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { RootNavigation } from '../../../Routes';

interface Props {
    navigation: RootNavigation;
}

export default function CloneSuspicions({ navigation }: Props) {
    const [cloneSuspicions, setCloneSuspicions] = useState<BaseCloneEvent[]>([]);

    const [selectedCloneEvent, setSelectedCloneEvent] = useState<BaseCloneEvent>();
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    const route = useRoute<RouteProp<ClonedPlateParamList, 'CloneSuspicions'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useState<GenericPaginatedParameters>({
        page: Math.max(route.params.page - 1, 0),
        textFilter: route.params.textFilter ?? '',
        limit: Number(route.params.limit) || 25,
    });
    const [totalItems, setTotalItems] = useState(0);
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['getPaginatedCloneEvents', filters],
        queryFn: () => clonedPlateService.getPaginatedCloneEvents(filters),
        enabled: !!authenticatedUser,
    });

    useEffect(() => {
        if (!data) {
            return;
        }
        setCloneSuspicions(data.rows);
        setTotalItems(data.count);
    }, [data]);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            setTotalItems(0);
            refetch();
        }
    }, [isFocused, filters]));

    return (
        <Container>
            <Header
                title={translate('cloneSuspect')}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredCloneSuspects')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter ?? ''}
                        onChange={text => {
                            setTextFilter(text);
                        }}
                        placeholder={translate('typeToSearch')}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        onRowClicked={(row) => {
                            setSelectedCloneEvent(row);
                            setIsModalVisible(true);
                        }}
                        pointerOnHover
                        progressPending={isLoading}
                        columns={[
                            {
                                name: 'ID',
                                selector: row => row.id,
                                grow: 0
                            },
                            {
                                name: translate('plate'),
                                selector: row => row.plate,
                                wrap: true,
                            },
                            {
                                name: translate('model'),
                                selector: row => row.model ?? '',
                                wrap: true,
                            },
                            {
                                name: translate('registeredSpeed'),
                                selector: row => `${row.speed}km/h`,
                                wrap: true,
                            },
                            {
                                name: translate('captureDate'),
                                selector: row => moment(row.detectedOn).format('DD/MM/YYYY'),
                                wrap: true,
                            },
                            {
                                name: translate('action'),
                                button: true,
                                cell: row => <DropdownButton actions={[{
                                    content: translate('editInformation'),
                                    onPress: () => {
                                        setSelectedCloneEvent(row);
                                        setIsModalVisible(true);
                                    }
                                }]} />
                            }
                        ]}
                        data={cloneSuspicions}
                    />}
                    pagination={<Pagination
                        totalItems={totalItems}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
            <CloneSuspicionModal
                cloneEventId={selectedCloneEvent?.id}
                isModalVisible={isModalVisible}
                setModalVisible={setIsModalVisible}
                onClose={async () => {
                    setIsModalVisible(false);
                    setSelectedCloneEvent(undefined);
                    await refetch();
                }} />
        </Container>
    );
}
